/* eslint-disable react/no-unknown-property */
import { useGLTF } from '@react-three/drei'
import { ThreeEvent } from '@react-three/fiber'
import React, { Fragment, useEffect, useMemo } from 'react'
import * as THREE from 'three'

import HitBox, {
  ControlOptions,
  INTERACTION_HOVER,
  INTERACTION_POINT,
  ModelControlsInterface,
} from './hitbox'
import { GLTFData } from './types'
import { PointerType, usePointerType } from './use-pointer-type'

type GLTFNodes = {
  [key: string]: THREE.Mesh
}

type GLTFMaterials = {
  [key: string]: THREE.Material
}

type GLTFResult = {
  nodes: GLTFNodes
  materials: GLTFMaterials
}

export interface GLTFBuilderProps {
  gltfSrc: string
  visible?: boolean
  modelControl: ModelControlsInterface
  getHighlightedTarget?: (arg: string) => void
  getNodes?: (nodes: GLTFNodes) => void
  modelLoaded?: (arg: boolean) => void
  interactionMode?: INTERACTION_HOVER | INTERACTION_POINT
  disableCameraLoadingCntrl?: boolean
  isMoving: boolean
  requireActionForHover: boolean
  onGLTFDataFound?: (data: GLTFData) => void
}

function GLTFBuilder({
  gltfSrc,
  modelControl,
  visible = true,
  getHighlightedTarget,
  getNodes,
  modelLoaded,
  interactionMode = 'hover',
  disableCameraLoadingCntrl = false,
  isMoving,
  requireActionForHover,
  onGLTFDataFound,
}: GLTFBuilderProps) {
  const gltfData = useGLTF(gltfSrc)
  const { nodes, materials } = gltfData as unknown as GLTFResult
  const pointerType = usePointerType()

  const GROUP = 'group'
  const MESH = 'mesh'
  const SCENE = 'scene'

  const validateAction = React.useCallback(
    (controlOptions?: ControlOptions) =>
      !requireActionForHover ||
      !!(controlOptions?.action && controlOptions?.actionTarget),
    []
  )

  const handleOnDoubleClick = React.useCallback(
    (e: ThreeEvent<MouseEvent>) => {
      e.stopPropagation()
      const obj = e.object as THREE.Mesh
      const cntrl = modelControl ? modelControl[obj.name] : undefined
      if (cntrl?.onClick && cntrl.id) {
        cntrl.onClick(cntrl)
      }
    },
    [modelControl]
  )

  const handleOnPointerOver = React.useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      e.stopPropagation()
      const obj = e.object as THREE.Mesh
      const objMaterial = obj.material as THREE.MeshStandardMaterial
      if (modelControl) {
        const hover = modelControl[obj.name]?.hoverColor
        const canAction = validateAction(modelControl[obj.name])
        if (!requireActionForHover || (hover && canAction)) {
          if (getHighlightedTarget) {
            getHighlightedTarget(modelControl[obj.name]?.id || '')
          }
          if (!hover) {
            return
          }
          obj.visible = true
          objMaterial.color.set(hover ?? new THREE.Color(255, 255, 255))
          objMaterial.transparent = true
          objMaterial.opacity = 0.7
        }
      }
    },
    [modelControl, validateAction]
  )

  const isTouchOrPen = useMemo(
    () => pointerType === PointerType.Touch || pointerType === PointerType.Pen,
    [pointerType]
  )

  useEffect(() => {
    if (!isTouchOrPen && isMoving && getHighlightedTarget) {
      getHighlightedTarget('')
    }
  }, [isMoving])

  useEffect(() => {
    onGLTFDataFound?.(gltfData)
  }, [gltfData, onGLTFDataFound])

  const handleOnPointerOut = React.useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      e.stopPropagation()
      const obj = e.object as THREE.Mesh
      if (modelControl) {
        if (
          modelControl[obj.name]?.hoverColor &&
          !modelControl[obj.name]?.hoverActive
        ) {
          obj.visible = false
        }
        if (getHighlightedTarget) {
          getHighlightedTarget('')
        }
      }
    },
    [modelControl]
  )

  const createMesh = (nodeMesh: THREE.Mesh, isInGroup = false) => {
    if (isInGroup) {
      return (
        <mesh
          key={`group-mesh-${nodeMesh.name}`}
          name={`group-mesh-${nodeMesh.name}`}
          geometry={nodeMesh.geometry}
          material={nodeMesh.material}
          position={nodeMesh.position}
          rotation={nodeMesh.rotation}
          scale={nodeMesh.scale}
        />
      )
    }

    return (
      <mesh
        key={`solo-mesh-${nodeMesh.name}`}
        name={nodeMesh.name}
        geometry={nodeMesh.geometry}
        material={nodeMesh.material}
        position={nodeMesh.position}
        rotation={nodeMesh.rotation}
        scale={nodeMesh.scale}
        onDoubleClick={(e) => handleOnDoubleClick(e)}
      />
    )
  }

  const connectedOrNotMoving = useMemo(
    () =>
      isTouchOrPen ||
      (!disableCameraLoadingCntrl && !isMoving) ||
      disableCameraLoadingCntrl,
    [isMoving, disableCameraLoadingCntrl, isTouchOrPen]
  )


  const createGroupChildren = (nodeMeshs: THREE.Mesh[]) =>
    nodeMeshs.map((nodeMesh) => {
      if (nodeMesh.geometry?.attributes?.position?.count >= 0) {
        return createMesh(nodeMesh, true)
      }
      return null
    })

  const createGroup = (nodeGroup: THREE.Mesh) => {
    const currentModelControl = modelControl[nodeGroup.children[0].name]

    return (
      <Fragment key={`node-group-wrapper-${nodeGroup.name}`}>
        {(!requireActionForHover ||
          (currentModelControl?.action && currentModelControl?.actionTarget)) &&
          (currentModelControl?.hoverActive || connectedOrNotMoving) && (
            <HitBox
              interactionMode={interactionMode}
              key={`hitbox-${nodeGroup.name}`}
              handleOnDoubleClick={handleOnDoubleClick}
              handleOnPointerOver={
                connectedOrNotMoving &&
                (!requireActionForHover || currentModelControl?.hoverColor)
                  ? handleOnPointerOver
                  : () => {}
              }
              handleOnPointerOut={
                !requireActionForHover || currentModelControl?.hoverColor
                  ? handleOnPointerOut
                  : () => {}
              }
              isVisible={currentModelControl?.hoverActive}
              materialColor={currentModelControl?.hoverColor}
              node={nodeGroup}
            />
          )}
        <group
          key={`group-${nodeGroup.name}`}
          name={`group-${nodeGroup.name}`}
          rotation={nodeGroup.rotation}
          position={nodeGroup.position}
          scale={nodeGroup?.scale || 1}
        >
          {nodeGroup.children &&
            createGroupChildren(nodeGroup.children as THREE.Mesh[])}
        </group>
      </Fragment>
    )
  }

  const getModelObjectFromNodes = (fromNodes: GLTFNodes, key: string) => {
    if (fromNodes[key].name.toLocaleLowerCase() === SCENE) {
      return null
    }
    if (fromNodes[key].type.toLowerCase() === GROUP) {
      return createGroup(fromNodes[key])
    }
    if (
      fromNodes[key].type.toLowerCase() === MESH &&
      fromNodes[key].parent?.name.toLocaleLowerCase() === SCENE
    ) {
      return createMesh(fromNodes[key])
    }
    return null
  }

  const buildModel = React.useMemo(() => {
    if (nodes && materials && modelControl) {
      return Object.keys(nodes)
        .map((key) => getModelObjectFromNodes(nodes, key))
        .filter((node) => node)
    }

    return <div>GTLT is invalid.</div>
  }, [nodes, materials, modelControl, connectedOrNotMoving])

  React.useEffect(() => {
    useGLTF.preload(gltfSrc)
  }, [gltfSrc])

  React.useEffect(() => {
    if (nodes) {
      getNodes?.(nodes)
    }
  }, [nodes])

  React.useEffect(() => {
    if (buildModel && modelLoaded) {
      modelLoaded(true)
    }
  }, [buildModel])

  return (
    <group visible={visible} dispose={null}>
      {buildModel}
    </group>
  )
}

export default GLTFBuilder
